import React, { useEffect, useRef, useState } from "react";
import "../Login/login.css";
import HttpCommon from "../../Utils/http-common";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import swal from "sweetalert";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import FormGroup from "../Common/FormGroup";
import InputField from "../Common/InputField";
import PasswordInput from "../Common/FormGroup/PasswordInput";
import ReactGA from "react-ga4";
import { useTheme } from "@mui/material/styles";

const defaultValue = {
  email: "",
  password: "",
};

const emailOnlySchema = Yup.object({
  email: Yup.string().email(`Invalid User format`).required("User is required"),
});

const passwordLoginSchema = Yup.object({
  email: Yup.string().email(`Invalid User format`).required("User is required"),
  password: Yup.string().required("Invalid Password"),
});

async function loginUser(Values) {
  const res = await HttpCommon.post("/user/userLogin", Values);
  return res;
}

function Login() {
  const formRef = useRef();
  const [check, setCheck] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const [initialValue, setInitialValue] = useState(defaultValue);
  const [emailStatus, setEmailStatus] = useState("idle"); // idle|checking|whitelist|password|not_found
  const [sendingLink, setSendingLink] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("password") !== null) {
      setInitialValue({
        email: localStorage.getItem("username"),
        password: localStorage.getItem("password"),
      });
      setEmailStatus("password");
    }
    setCheck(localStorage.getItem("checkbox") || false);
  }, []);

  const checkEmail = async (email, { setFieldError, setErrors } = {}) => {
    const trimmed = String(email || "").trim();
    if (!trimmed) return;
    setEmailStatus("checking");
    try {
      const res = await HttpCommon.post("/user/login-email-check", {
        email: trimmed,
      });
      if (res?.data?.success === 0 && res?.data?.message === "Invalid Domain") {
        swal(
          "Invalid Domain!",
          "You are trying to login with invalid domain!",
          "error",
        );
        setEmailStatus("idle");
        return;
      }
      if (res?.data?.success === 0 && res?.data?.message === "Partner not found!") {
        swal("Error!", res?.data?.message, "error");
        setEmailStatus("idle");
        return;
      }
      const status = res?.data?.status || "not_found";
      // Whitelist → send-link step; everyone else (known or unknown) → password step
      setEmailStatus(
        status === "whitelist"
          ? "whitelist"
          : status === "password"
            ? "password"
            : "not_found",
      );
      // Clear any prior email field error (e.g. old "Invalid Email")
      setErrors?.({});
    } catch (err) {
      setEmailStatus("idle");
      if (err.code === "ERR_NETWORK") {
        swal("Network Error!", "Failed to connect server.", "error");
      } else {
        swal("Error!", err?.response?.data?.message || "Something went wrong", "error");
      }
    }
  };

  const handleSendLink = async (email) => {
    setSendingLink(true);
    try {
      const res = await HttpCommon.post("/user/whitelist-send-link", { email });
      if (res?.data?.success === 1) {
        swal("Check your email", res?.data?.message || "Link sent.", "success");
      } else {
        swal("Unable to send link", res?.data?.message || "Please try again.", "error");
      }
    } catch (err) {
      swal("Error!", err?.response?.data?.message || "Something went wrong", "error");
    } finally {
      setSendingLink(false);
    }
  };

  const handleSubmit = async (Values, { setFieldError, setErrors, setSubmitting }) => {
    try {
      if (emailStatus === "idle" || emailStatus === "checking") {
        await checkEmail(Values.email, { setFieldError, setErrors });
        return;
      }
      if (emailStatus === "whitelist") {
        await handleSendLink(Values.email);
        return;
      }

      // password | not_found → normal password login
      const res = await loginUser(Values);
      if (res?.data?.success === 1) {
        const user_id = res?.data?.user_id;
        const token = res?.data?.token;
        navigate(`/dashboard/?id=${user_id}&token=${token}&subuser=false`);
        ReactGA.gtag("event", "login_user", { user_id });
        ReactGA.set({ userId: user_id });
      } else if (res?.data?.message === "Invalid Email") {
        setEmailStatus("not_found");
        setFieldError("password", "Invalid email or password");
      } else if (res?.data?.message === "Invalid Domain") {
        swal(
          "Invalid Domain!",
          "You are trying to login with invalid domain!",
          "error",
        );
      } else {
        setFieldError("password", res?.data?.message);
      }
    } catch (err) {
      if (err.code === "ERR_NETWORK") {
        swal("Network Error!", "Failed to connect server.", "error");
      }
      if (err?.response?.success === false) {
        swal(
          "Invalid Credentials!",
          err?.response?.data?.message || "Something went wrong",
          "error",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleMember = () => {
    navigate("/register");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const showPasswordStep =
    emailStatus === "password" || emailStatus === "not_found";

  const validationSchema = showPasswordStep
    ? passwordLoginSchema
    : emailOnlySchema;

  const submitLabel = showPasswordStep
    ? "Log In"
    : emailStatus === "whitelist"
      ? sendingLink
        ? "Sending…"
        : "Send link"
      : emailStatus === "checking"
        ? "Checking…"
        : "Continue";

  return (
    <div className="main">
      <div className="container">
        <Formik
          initialValues={initialValue}
          enableReinitialize
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          innerRef={formRef}
        >
          {({ getFieldProps, isSubmitting, values, setFieldError, setFieldValue, setErrors }) => (
            <Form>
              <h1>Log In to your account</h1>
              <LoginStack>
              <FormGroup
                label="Username"
                name="email"
                className="loginField"
                svgPath={require("../../Assets/Images/user.png")}
                requiredField
              >
                <InputField
                  type="text"
                  name="email"
                  className="textarea"
                  {...getFieldProps("email")}
                  onBlur={async (e) => {
                    getFieldProps("email").onBlur(e);
                    if (values.email && emailStatus === "idle") {
                      await checkEmail(values.email, { setFieldError, setErrors });
                    }
                  }}
                  onChange={(e) => {
                    setFieldValue("email", e.target.value);
                    if (emailStatus !== "idle" && emailStatus !== "checking") {
                      setEmailStatus("idle");
                      setFieldValue("password", "");
                    }
                  }}
                />
              </FormGroup>

              {emailStatus === "whitelist" && (
                <WhitelistHelp role="status">
                  <WhitelistHelpIcon aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <rect
                        x="3"
                        y="5"
                        width="18"
                        height="14"
                        rx="3"
                        stroke="currentColor"
                        strokeWidth="1.75"
                      />
                      <path
                        d="M4 7l8 6 8-6"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </WhitelistHelpIcon>
                  <WhitelistHelpCopy>
                    <span>We&apos;ll email you a secure sign-in link.</span>
                    <small>
                      Open the link to log in, then set up your password in the app.
                    </small>
                  </WhitelistHelpCopy>
                </WhitelistHelp>
              )}

              {showPasswordStep && (
                <FormGroup
                  label="Password"
                  name="password"
                  className="loginField"
                  svgPath={require("../../Assets/Images/lock.png")}
                  requiredField
                >
                  <PasswordInput {...getFieldProps("password")} />
                </FormGroup>
              )}

              {emailStatus === "not_found" && (
                <LoginHelp role="status">
                  <span>No account found for this email</span>
                  <small>
                    Double-check the address, then enter your password if you already
                    have an account — or create one below.
                  </small>
                </LoginHelp>
              )}

              {showPasswordStep && (
                <div className="middle">
                  <div className="left">
                    <input
                      type="checkbox"
                      id="check"
                      onChange={(e) => {
                        setCheck(e.target.checked);
                      }}
                      checked={check}
                      style={{ margin: 0 }}
                    />
                    <label htmlFor="check"> Remember Me</label>
                  </div>
                  <div className="right">
                    <label>
                      <a href="/forget-password">Forgot password?</a>
                    </label>
                  </div>
                </div>
              )}
              </LoginStack>

              {isSubmitting || sendingLink || emailStatus === "checking" ? (
                <button
                  className="btn1"
                  style={{
                    display: "grid",
                    backgroundColor: ` ${theme.palette.primary.main}`,
                  }}
                  type="submit"
                  disabled
                >
                  <Loader />
                </button>
              ) : (
                <button
                  className="btn1"
                  type="submit"
                  style={{ backgroundColor: ` ${theme.palette.primary.main}` }}
                >
                  {submitLabel}
                </button>
              )}

              <p id="account">Not a member? Create an account below</p>
              <button
                className="btn12"
                onClick={handleMember}
                style={{ backgroundColor: ` ${theme.palette.secondary.main}` }}
                type="button"
              >
                Become Member
              </button>
              <a id="l4" href="/contactus">
                Need Help ?
              </a>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default Login;

const LoginStack = styled.div`
  width: 330px;
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;

  .loginField {
    width: 100%;
  }

  .loginField input,
  .loginField .textarea {
    width: 100% !important;
    max-width: 100%;
    box-sizing: border-box;
  }

  .middle {
    width: 100%;
    box-sizing: border-box;
  }
`;

const WhitelistHelp = styled.div`
  width: 100%;
  margin: 6px 0 16px;
  display: grid;
  grid-template-columns: 36px 1fr;
  column-gap: 10px;
  align-items: start;
  box-sizing: border-box;
`;

const WhitelistHelpIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(6, 71, 108, 1);
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(36, 159, 218, 0.4);
  box-sizing: border-box;
`;

const WhitelistHelpCopy = styled.div`
  min-width: 0;
  text-align: left;
  padding-top: 1px;

  span {
    display: block;
    color: rgba(6, 71, 108, 1);
    font-size: 13.5px;
    font-weight: 600;
    line-height: 1.35;
  }

  small {
    display: block;
    margin-top: 3px;
    color: #35586b;
    font-size: 12px;
    line-height: 1.4;
  }
`;

const LoginHelp = styled.div`
  width: 100%;
  margin: 4px 0 10px;
  text-align: left;
  box-sizing: border-box;

  span {
    display: block;
    color: rgba(6, 71, 108, 1);
    font-size: 13.5px;
    font-weight: 600;
    line-height: 1.35;
  }

  small {
    display: block;
    margin-top: 3px;
    color: #35586b;
    font-size: 12px;
    line-height: 1.4;
  }
`;

const Loader = styled.div`
  justify-self: center;
  border: 5px solid #f3f3f3;
  border-radius: 50%;
  border-top: 5px solid #3498db;
  width: 12px;
  height: 12px;
  -webkit-animation: spin 2s linear infinite; /* Safari */
  animation: spin 2s linear infinite;

  @-webkit-keyframes spin {
    0% {
      -webkit-transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
